import type { Document } from "../document/Document";
import type { Layer, RasterLayer } from "../document/Layer";
import type { Command } from "./Command";
import { setPos } from "./MoveLayerCommand";

// FloatSelectionCommand — atomically:
//   apply:  write "zeroed" (post-float) pixels to source, insert float layer
//   invert: write original (pre-float) pixels back, remove float layer
//
// Both pixel snapshots are captured by MoveTool BEFORE emitting this command.
// This makes float + move fully undoable in one undo step.

/**
 * Original (pre-drag) position of an extra selected/linked layer moving
 * alongside the float+move gesture (audit i4/i56). Recorded here — at
 * pointer-down, before any move happens — purely so FloatSelectionUndoCommand
 * (this command's invert) can snap those layers back to their exact starting
 * position on undo, regardless of how far MoveLayerAndSelectionCommand moved
 * them at pointer-up. This command's own `apply` never touches them.
 */
export interface ExtraLayerOriginal {
  readonly id: string;
  readonly x: number;
  readonly y: number;
}

export class FloatSelectionCommand implements Command {
  readonly kind = "float-selection";

  constructor(
    /** ID of the source raster layer */
    private readonly sourceLayerId: string,
    /** Pixel data of source layer BEFORE the float (pre-zero snapshot) */
    private readonly pixelsBefore: Uint8ClampedArray,
    /** Pixel data of source layer AFTER the float (post-zero snapshot) */
    private readonly pixelsAfter: Uint8ClampedArray,
    /** The floating layer to insert */
    private readonly floatLayer: RasterLayer,
    /** Other selected/linked layers' original positions (see ExtraLayerOriginal). */
    private readonly extraOriginal: readonly ExtraLayerOriginal[] = [],
  ) {}

  apply(doc: Document): Document {
    // Write post-float (zeroed) pixels back (on redo they were wiped; on first apply
    // they are already zeroed — this is idempotent because they match current state)
    const src = findRaster(doc.layers, this.sourceLayerId);
    if (src) {
      src.pixels.data.set(this.pixelsAfter);
      src.pixels.markDirty();
    }
    return {
      ...doc,
      layers: [...doc.layers, this.floatLayer],
      activeLayerId: this.floatLayer.id,
    };
  }

  invert(_before: Document, _after: Document): Command {
    return new FloatSelectionUndoCommand(
      this.sourceLayerId,
      this.pixelsBefore,
      this.pixelsAfter,
      this.floatLayer,
      this.extraOriginal,
    );
  }
}

/** The undo side: restores original pixels, removes float layer. */
class FloatSelectionUndoCommand implements Command {
  readonly kind = "float-selection-undo";

  constructor(
    private readonly sourceLayerId: string,
    private readonly pixelsBefore: Uint8ClampedArray,
    private readonly pixelsAfter: Uint8ClampedArray,
    private readonly floatLayer: RasterLayer,
    private readonly extraOriginal: readonly ExtraLayerOriginal[] = [],
  ) {}

  apply(doc: Document): Document {
    // Restore original (pre-float) pixels
    const src = findRaster(doc.layers, this.sourceLayerId);
    if (src) {
      src.pixels.data.set(this.pixelsBefore);
      src.pixels.markDirty();
    }
    // Remove the floating layer
    let layers = removeLayer(doc.layers, this.floatLayer.id);
    // Snap any extra selected/linked layers back to their pre-gesture position
    // so a single undo reverts the whole multi-layer float+move (i4/i56).
    for (const e of this.extraOriginal) {
      layers = setPos(layers, e.id, e.x, e.y);
    }
    return {
      ...doc,
      layers,
      activeLayerId: this.sourceLayerId,
    };
  }

  invert(_before: Document, _after: Document): Command {
    // Redo: re-apply the original float command
    return new FloatSelectionCommand(
      this.sourceLayerId,
      this.pixelsBefore,
      this.pixelsAfter,
      this.floatLayer,
      this.extraOriginal,
    );
  }
}

function findRaster(layers: readonly Layer[], id: string): RasterLayer | null {
  for (const l of layers) {
    if (l.id === id && l.kind === "raster") return l;
    if (l.kind === "group") {
      const inner = findRaster(l.children, id);
      if (inner) return inner;
    }
  }
  return null;
}

function removeLayer(layers: readonly Layer[], id: string): Layer[] {
  const out: Layer[] = [];
  for (const l of layers) {
    if (l.id === id) continue;
    if (l.kind === "group") {
      out.push({ ...l, children: removeLayer(l.children, id) });
    } else {
      out.push(l);
    }
  }
  return out;
}
